/**
 * Viewport width — the one signal the layout collapses on.
 *
 * WIDTH, not the user agent, and not `pointer: coarse`:
 *  - A UA string is a guess about a device, and a wrong one is unfixable by
 *    the user. Nothing here needs to know what the device IS, only how much
 *    room there is to lay out in.
 *  - `pointer: coarse` (core/canvas.ts `COARSE_POINTER`) answers a DIFFERENT
 *    question — "is the primary pointer a finger?" — and the two disagree in
 *    both directions: a touch laptop is coarse but wide, a desktop browser
 *    dragged to half the screen is fine-pointered but narrow. Overlapping
 *    toolbars are a width problem, so width is what decides.
 *  - Kola also runs inside the rainvic-tool desktop app's `<webview>`
 *    (components/Kola/KolaView), which is always desktop-width — so it gets
 *    the desktop layout for free, with no Electron-specific branch.
 *
 * `NARROW_MAX_WIDTH_PX` is the JS twin of the `@media (max-width: …)` blocks
 * in ui/app.css: keep the two in step (app.css's "narrow viewport" section
 * names this constant). CSS handles anything that is purely presentational;
 * this hook exists for the collapses that change WHAT IS RENDERED — a row of
 * toolbar buttons becoming one menu is a different tree, not a restyle, and
 * `display: none` on the wide version would leave its buttons in the
 * accessibility tree and its tooltips in the DOM.
 */
import { useEffect, useState, type RefObject } from "react";

/**
 * At or below this width the board's two floating bars cannot sit side by
 * side: measured on an iPhone 13 (390 px) they want 262 px + 294 px against
 * 366 px of usable width, so the right bar lands on top of the board title
 * and the stage chip. 768 px is the conventional tablet-portrait breakpoint
 * and sits comfortably above the ~580 px where the two bars actually start
 * to touch, leaving room for a long board title.
 */
export const NARROW_MAX_WIDTH_PX = 768;

export const NARROW_MEDIA_QUERY = `(max-width: ${NARROW_MAX_WIDTH_PX}px)`;

/**
 * Current answer, read once. For a value that follows a resize (or a phone
 * rotating), use the hook below.
 */
export function isNarrowViewport(): boolean {
  return typeof window !== "undefined" && window.matchMedia?.(NARROW_MEDIA_QUERY).matches === true;
}

/**
 * True while the viewport is narrow, re-rendering on the crossing only —
 * this listens to the media query, never to `resize`, so dragging a desktop
 * window wider costs nothing until it actually crosses the breakpoint.
 */
export function useIsNarrowViewport(): boolean {
  const [narrow, setNarrow] = useState(isNarrowViewport);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const query = window.matchMedia(NARROW_MEDIA_QUERY);
    // Re-read on mount: the width can have changed between the initial state
    // above and this effect (a rotation during hydration, a restored window).
    setNarrow(query.matches);
    const onChange = (event: MediaQueryListEvent) => setNarrow(event.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  return narrow;
}

/** Embedded boards collapse against their available pane, not the host window. */
export function useIsNarrowContainer(ref: RefObject<HTMLElement | null>): boolean {
  const [narrow, setNarrow] = useState(isNarrowViewport);
  useEffect(() => {
    const element = ref.current;
    if (element === null) return;
    const update = () => setNarrow(element.clientWidth <= NARROW_MAX_WIDTH_PX);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref]);
  return narrow;
}
